import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  InputBase,
  Button,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { IoMdNotificationsOutline } from "react-icons/io";
import {
  LuSearch,
  MdTaskAlt,
  BiHelpCircle,
  LuChevronDown,
  CiLocationOn,
  FaLocationDot,
} from "@/Components/Icons/Icons";
import logo from "@/Assets/logo.png";
import { useNavigate } from "react-router-dom";
import { navurl } from "@/Config/Routeconfig/NavigationUrl";
import UserAvatarIcon from "@/Components/Icons/AccountCircleIcon";
import { useUser } from "@/Context/UserContext";
import { useGetCityMasterQuery } from "@/redux/Masters/Masters"; // update path if needed
import { useGlobalFilter } from "@/Context/GlobalFilterContext"; // import this

export default function Header({ onToggleDrawer }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [username, setUsername] = useState(localStorage.getItem("userName") || "User");
  const [yearAnchorEl, setYearAnchorEl] = useState(null); // For year dropdown
  const [selectedYear, setSelectedYear] = useState("2025-2026");
  const { data: cityList = [], isLoading: isCityLoading } =
    useGetCityMasterQuery();

  const open = Boolean(anchorEl);
  const yearMenuOpen = Boolean(yearAnchorEl);
  const { financialYear, setFinancialYear, city, setCity } = useGlobalFilter(); // 👈 use context

  const navigate = useNavigate();
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { clearAll } = useUser();

  const [locationAnchorEl, setLocationAnchorEl] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState("Chennai"); // default location

  const locationMenuOpen = Boolean(locationAnchorEl);

  // sample list of locations
  const locations = cityList?.map((city) => city?.name) || [];
  // useEffect(() => {
  //   const storedName = localStorage.getItem("userName");
  //   if (storedName) setUsername(storedName);
  // }, []);
  const handleLocationClick = (event) =>
    setLocationAnchorEl(event.currentTarget);

  // Automatically generate years from 2015 to current year
  // Generate financial years from 2015 to current year, in descending order
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear + 1; y >= 2016; y--) { 
    years.push(`${y - 1}-${y}`);
  }

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleYearClick = (event) => setYearAnchorEl(event.currentTarget);
  const handleYearClose = (year) => {
    if (year) {
      setSelectedYear(year);
      setFinancialYear(year); // ✅ update context
    }
    setYearAnchorEl(null);
  };

  const handleLocationClose = (location) => {
    if (location) {
      setSelectedLocation(location);
  
      // find the selected city object
      const selectedCityObj = cityList.find((c) => c.name === location);
  
      // ✅ store full city details in context
      if (selectedCityObj) {
        setCity({
          id: selectedCityObj.id?.id || "", // ✅ extract the actual cityId string
          name: selectedCityObj.name,
        });
      }
    }
    setLocationAnchorEl(null);
  };
  
  const handleLogout = () => {
    localStorage.clear();
    handleMenuClose();
    clearAll();
    navigate(navurl.Login);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "var(--brand-800)",
        boxShadow: "none",
        height: 65,
        zIndex: 11,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          px: isSmallScreen ? 1 : 2,
        }}
      >
        {/* Left: Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: isSmallScreen ? "auto" : 200,
          }}
        >
          <Box
            className="white-image"
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              height: 38,
              mr: isSmallScreen ? 1 : 2,
              width: "auto",
            }}
          />
        </Box>

        {/* Middle: Search */}
        {!isSmallScreen && (
          <Box
            sx={{
              minWidth: isMediumScreen ? 200 : 320,
              mx: isMediumScreen ? 1 : 4,
              display: "flex",
              alignItems: "center",
              backgroundColor: "var(--brand-800)",
              borderRadius: 2,
              border: "1px solid var(--brand-500)",
              px: 1,
              flexGrow: isMediumScreen ? 1 : 0,
            }}
          >
            <LuSearch
              style={{
                color: "var(--brand-50)",
                marginRight: "12px",
                fontSize: "24px",
              }}
            />
            <InputBase
              placeholder="Search anything"
              sx={{
                color: "var(--brand-300)",
                width: "100%",
              }}
            />
          </Box>
        )}

        {/* Right Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isSmallScreen ? 0.5 : "var(--v-gap-sm)",
            ml: "auto",
          }}
        >
          {/* Financial Year Dropdown */}
          {!isSmallScreen && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={handleYearClick}
                sx={{
                  color: "var(--gray-0)",
                  border: "1px solid var(--brand-500)",
                  width: isMediumScreen ? 120 : 155,
                  borderRadius: "8px",
                  height: 35,
                  position: "relative",
                  pr: 3.5,
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <MdTaskAlt size={20} />
                  {isMediumScreen ? selectedYear.split("-")[1] : selectedYear}
                </Box>
                <LuChevronDown
                  size={15}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              </Button>

              <Menu
                anchorEl={yearAnchorEl}
                open={yearMenuOpen}
                onClose={() => handleYearClose(null)}
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    mt: 1,
                    minWidth: 155,
                    backgroundColor: "var(--gray-100)", // white background
                    color: "var(--brand-800)", // brand text color
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                    maxHeight: 220, // limit dropdown height
                    overflowY: "auto", // enable scrolling
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "var(--brand-400)",
                      borderRadius: "10px",
                    },
                  },
                }}
                MenuListProps={{
                  sx: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    p: 1,
                  },
                }}
              >
                {years.map((year) => (
                  <MenuItem
                    key={year}
                    onClick={() => handleYearClose(year)}
                    sx={{
                      justifyContent: "center",
                      color: "var(--brand-800)",
                      fontWeight: year === selectedYear ? "bold" : "normal",
                      borderRadius: "8px",
                      width: "90%",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "var(--brand-800)",
                        color: "var(--gray-0)",
                      },
                    }}
                  >
                    {year}
                  </MenuItem>
                ))}
              </Menu>
              {/* Location Dropdown */}
              <Button
                variant="outlined"
                size="small"
                onClick={handleLocationClick}
                sx={{
                  color: "var(--gray-0)",
                  border: "1px solid var(--brand-500)",
                  width: isMediumScreen ? 120 : 150,
                  borderRadius: "8px",
                  height: 35,
                  position: "relative",
                  pr: 5,
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <FaLocationDot size={18} /> {/* you can replace this icon */}
                  {isMediumScreen
                    ? selectedLocation.slice(0, 6)
                    : selectedLocation}
                </Box>
                <LuChevronDown
                  size={15}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              </Button>

              <Menu
                anchorEl={locationAnchorEl}
                open={locationMenuOpen}
                onClose={() => handleLocationClose(null)}
                PaperProps={{
                  sx: {
                    borderRadius: 1.5,
                    mt: 0.5, // smaller gap from button
                    minWidth: 120, // ✅ same as button width
                    backgroundColor: "var(--gray-100)",
                    color: "var(--brand-800)",
                    boxShadow: "0px 3px 8px rgba(0,0,0,0.12)",
                    maxHeight: 220, // ✅ shorter dropdown
                    overflowY: "auto",
                    p: 0, // remove inner padding
                    "& .MuiMenuItem-root": {
                      fontSize: "0.8rem", // ✅ smaller text
                      py: 0.5, // ✅ reduce row height
                      px: 1.5,
                      minHeight: "28px", // ✅ compact rows
                    },
                  },
                }}
                MenuListProps={{
                  sx: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  },
                }}
              >
                {locations.map((location) => (
                  <MenuItem
                    key={location}
                    onClick={() => handleLocationClose(location)}
                    sx={{
                      justifyContent: "center",
                      color: "var(--brand-800)",
                      fontWeight:
                        location === selectedLocation ? "bold" : "normal",
                      borderRadius: "8px",
                      width: "90%",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "var(--brand-800)",
                        color: "var(--gray-0)",
                      },
                    }}
                  >
                    {location}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {/* Help, Notification, User */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "var(--v-gap-sm)",
            }}
          >
            <IconButton
              color="inherit"
              size={isSmallScreen ? "small" : "medium"}
            >
              <BiHelpCircle />
            </IconButton>

            <IconButton
              color="inherit"
              size={isSmallScreen ? "small" : "medium"}
              sx={{ paddingLeft: 0 }}
            >
              <IoMdNotificationsOutline />
            </IconButton>

            <UserAvatarIcon
              onClick={handleMenuOpen}
              isOnline={true}
              user={{
                name: username,
                email: "x4T0P@example.com",
              }}
              size={isSmallScreen ? "small" : "medium"}
            />
          </Box>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{ mt: 1 }}
          >
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
